import { apiFetch } from "@/lib/api";
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Progress } from './ui/progress';
import { Play, Square, Clock, Timer, Activity, Target, Hourglass } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const activityTypes = [
  { value: 'general', label: 'General Work', color: 'blue' },
  { value: 'diagnosis', label: 'Diagnosis', color: 'purple' },
  { value: 'repair', label: 'Repair Work', color: 'green' },
  { value: 'testing', label: 'Testing', color: 'yellow' },
  { value: 'parts_installation', label: 'Parts Installation', color: 'orange' }
];

// Wall-clock time the job was actively worked on, merging overlapping technician sessions so
// parallel work by multiple technicians isn't double-counted (mirrors backend compute_elapsed_minutes)
const computeElapsedMinutes = (jobCard, now) => {
  const intervals = [];
  (jobCard.time_logs || []).forEach(log => {
    (log.sessions || []).forEach(s => {
      if (s.started_at && s.ended_at) {
        const start = new Date(s.started_at).getTime();
        const end = new Date(s.ended_at).getTime();
        if (!isNaN(start) && !isNaN(end)) intervals.push([start, end]);
      }
    });
  });
  (jobCard.active_sessions || []).forEach(s => {
    if (s.started_at) {
      const start = new Date(s.started_at).getTime();
      if (!isNaN(start)) intervals.push([start, now.getTime()]);
    }
  });
  if (intervals.length === 0) return 0;
  intervals.sort((a, b) => a[0] - b[0]);
  const merged = [intervals[0]];
  for (let i = 1; i < intervals.length; i++) {
    const [start, end] = intervals[i];
    const last = merged[merged.length - 1];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  const totalMs = merged.reduce((sum, [s, e]) => sum + (e - s), 0);
  return totalMs / 60000;
};

const TimeTracker = ({ jobCard, onUpdate }) => {
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [stopDialogSession, setStopDialogSession] = useState(null);
  const [technicianName, setTechnicianName] = useState('');
  const [activityType, setActivityType] = useState('general');
  const [sessionNotes, setSessionNotes] = useState('');
  const [breakTime, setBreakTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const activeSessions = jobCard.active_sessions || (jobCard.current_session ? [jobCard.current_session] : []);

  // Fetch employees when component mounts
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/employees`);
        setEmployees(response.data || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setEmployees([]);
      }
    };

    fetchEmployees();
  }, [backendUrl]);

  // Update current time every minute for live session tracking
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const startWorkSession = async () => {
    if (!technicianName) {
      toast.error('Please select a technician');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`${backendUrl}/api/jobcards/${jobCard.id}/start-work`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          technician_name: technicianName,
          activity_type: activityType,
          notes: sessionNotes
        }),
      });

      if (response.ok) {
        const updatedJobCard = await response.json();
        onUpdate(updatedJobCard);
        toast.success(`${technicianName} started work session`);
        setIsStartDialogOpen(false);
        setTechnicianName('');
        setSessionNotes('');
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error starting work session:', error);
      toast.error('Failed to start work session');
    } finally {
      setLoading(false);
    }
  };

  const stopWorkSession = async () => {
    if (!stopDialogSession) return;
    setLoading(true);
    try {
      const response = await apiFetch(`${backendUrl}/api/jobcards/${jobCard.id}/stop-work`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: stopDialogSession.id,
          notes: sessionNotes,
          break_time_minutes: breakTime
        }),
      });

      if (response.ok) {
        const updatedJobCard = await response.json();
        onUpdate(updatedJobCard);
        toast.success('Work session stopped and logged successfully');
        setStopDialogSession(null);
        setSessionNotes('');
        setBreakTime(0);
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error stopping work session:', error);
      toast.error('Failed to stop work session');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const safe = Math.max(0, Math.round(minutes || 0));
    const hours = Math.floor(safe / 60);
    const mins = safe % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const calculateSessionDuration = (session) => {
    if (!session?.started_at) return 0;
    const startTime = new Date(session.started_at);
    const duration = (currentTime - startTime) / (1000 * 60); // minutes
    return Math.max(0, duration);
  };

  const getActivityColor = (activity) => {
    const actType = activityTypes.find(at => at.value === activity);
    return actType ? actType.color : 'gray';
  };

  const calculateEfficiencyScore = () => {
    const estimated = jobCard.estimated_hours || 0;
    const actual = jobCard.actual_hours || 0;
    if (estimated === 0 || actual === 0) return null;

    const efficiency = (estimated / actual) * 100;
    return Math.min(efficiency, 200); // Cap at 200%
  };

  const getEfficiencyColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const efficiencyScore = calculateEfficiencyScore();
  const elapsedMinutes = computeElapsedMinutes(jobCard, currentTime);
  const activeTechNames = new Set(activeSessions.map(s => (s.technician_name || '').trim().toLowerCase()));

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5" />
          Time Tracking
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Session Status */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {activeSessions.length > 0 ? (
                  <Badge className="bg-green-100 text-green-800" data-testid="active-sessions-badge">
                    <Play className="h-3 w-3 mr-1" />
                    {activeSessions.length} ACTIVE {activeSessions.length > 1 ? 'SESSIONS' : 'SESSION'}
                  </Badge>
                ) : (
                  <Badge variant="outline" data-testid="no-active-session-badge">
                    <Square className="h-3 w-3 mr-1" />
                    NO ACTIVE SESSION
                  </Badge>
                )}
              </div>

              <Dialog open={isStartDialogOpen} onOpenChange={setIsStartDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={jobCard.is_on_hold}
                    className="text-green-600 hover:text-green-700"
                    data-testid="start-work-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Work
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="start-work-dialog">
                  <DialogHeader>
                    <DialogTitle>Start Work Session</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Technician Name *</Label>
                      <Select value={technicianName} onValueChange={setTechnicianName}>
                        <SelectTrigger data-testid="start-work-technician-select">
                          <SelectValue placeholder="Select technician" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees
                            .filter(employee =>
                              employee.status === 'active' &&
                              (employee.department === 'service' ||
                               employee.department === 'technical' ||
                               employee.role === 'technician' ||
                               employee.position?.toLowerCase().includes('technician') ||
                               employee.position?.toLowerCase().includes('mechanic') ||
                               employee.position?.toLowerCase().includes('advisor'))
                            )
                            .map(employee => (
                              <SelectItem key={employee.id} value={employee.name}>
                                {employee.name} - {employee.position || employee.role || employee.department}
                                {activeTechNames.has(employee.name.trim().toLowerCase()) ? ' (already active)' : ''}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {activeSessions.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">Other technicians are already working on this job card — you can start a parallel session too.</p>
                      )}
                    </div>

                    <div>
                      <Label>Activity Type</Label>
                      <Select value={activityType} onValueChange={setActivityType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {activityTypes.map(activity => (
                            <SelectItem key={activity.value} value={activity.value}>
                              {activity.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Session Notes</Label>
                      <Textarea
                        value={sessionNotes}
                        onChange={(e) => setSessionNotes(e.target.value)}
                        placeholder="What will you be working on?"
                        rows={3}
                        data-testid="start-work-notes-input"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsStartDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={startWorkSession} disabled={loading} data-testid="start-work-confirm-btn">
                        {loading ? 'Starting...' : 'Start Session'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Active Session Rows - one per technician */}
            {activeSessions.length > 0 && (
              <div className="space-y-2">
                {activeSessions.map((session) => (
                  <div key={session.id} className="bg-white p-3 rounded border" data-testid={`active-session-${session.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid grid-cols-2 gap-4 text-sm flex-1">
                        <div>
                          <span className="text-gray-600">Technician:</span>
                          <br />
                          <span className="font-medium">{session.technician_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Activity:</span>
                          <br />
                          <Badge className={`bg-${getActivityColor(session.activity_type)}-100 text-${getActivityColor(session.activity_type)}-800`}>
                            {activityTypes.find(a => a.value === session.activity_type)?.label}
                          </Badge>
                        </div>
                        <div>
                          <span className="text-gray-600">Started:</span>
                          <br />
                          <span>{new Date(session.started_at).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Live Duration:</span>
                          <br />
                          <span className="font-mono text-lg text-green-600">
                            {formatDuration(calculateSessionDuration(session))}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 shrink-0"
                        onClick={() => setStopDialogSession(session)}
                        data-testid={`stop-work-btn-${session.id}`}
                      >
                        <Square className="h-4 w-4 mr-2" />
                        Stop
                      </Button>
                    </div>
                    {session.notes && (
                      <div className="mt-3 p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Notes: </span>
                        <span className="text-sm">{session.notes}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stop Session Dialog */}
          <Dialog open={!!stopDialogSession} onOpenChange={(open) => { if (!open) { setStopDialogSession(null); setSessionNotes(''); setBreakTime(0); } }}>
            <DialogContent data-testid="stop-work-dialog">
              <DialogHeader>
                <DialogTitle>Stop Work Session</DialogTitle>
              </DialogHeader>
              {stopDialogSession && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded">
                    <div className="text-sm text-blue-800">
                      <strong>Session:</strong>
                      <br />
                      Technician: {stopDialogSession.technician_name}
                      <br />
                      Activity: {activityTypes.find(a => a.value === stopDialogSession.activity_type)?.label}
                      <br />
                      Duration: {formatDuration(calculateSessionDuration(stopDialogSession))}
                    </div>
                  </div>

                  <div>
                    <Label>Break Time (minutes)</Label>
                    <Input
                      type="number"
                      value={breakTime}
                      onChange={(e) => setBreakTime(Number(e.target.value))}
                      placeholder="0"
                      min="0"
                      data-testid="stop-work-break-input"
                    />
                  </div>

                  <div>
                    <Label>Session Notes</Label>
                    <Textarea
                      value={sessionNotes}
                      onChange={(e) => setSessionNotes(e.target.value)}
                      placeholder="Describe what was accomplished..."
                      rows={3}
                      data-testid="stop-work-notes-input"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setStopDialogSession(null)}>
                      Cancel
                    </Button>
                    <Button onClick={stopWorkSession} disabled={loading} data-testid="stop-work-confirm-btn">
                      {loading ? 'Stopping...' : 'Stop Session'}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Time Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Total Labor</span>
              </div>
              <div className="text-2xl font-bold text-blue-600 mt-1" data-testid="total-labor-minutes">
                {formatDuration(jobCard.total_worked_minutes || 0)}
              </div>
              <div className="text-xs text-gray-500">
                Billable: {formatDuration(jobCard.billable_minutes || 0)}
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Hourglass className="h-4 w-4 text-teal-500" />
                <span className="text-sm font-medium">Elapsed Time</span>
              </div>
              <div className="text-2xl font-bold text-teal-600 mt-1" data-testid="elapsed-time-minutes">
                {formatDuration(elapsedMinutes)}
              </div>
              <div className="text-xs text-gray-500">
                Job turnaround (parallel work not double-counted)
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium">Estimated</span>
              </div>
              <div className="text-2xl font-bold text-purple-600 mt-1">
                {jobCard.estimated_hours ? `${jobCard.estimated_hours}h` : '-'}
              </div>
              <div className="text-xs text-gray-500">
                Target time
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium">Efficiency</span>
              </div>
              <div className={`text-2xl font-bold mt-1 ${efficiencyScore ? getEfficiencyColor(efficiencyScore) : 'text-gray-400'}`}>
                {efficiencyScore ? `${Math.round(efficiencyScore)}%` : '-'}
              </div>
              <div className="text-xs text-gray-500">
                Est. vs Actual
              </div>
            </Card>
          </div>

          {/* Progress Bar */}
          {jobCard.estimated_hours && jobCard.actual_hours && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress vs Estimate</span>
                <span>{Math.round((jobCard.actual_hours / jobCard.estimated_hours) * 100)}%</span>
              </div>
              <Progress
                value={Math.min((jobCard.actual_hours / jobCard.estimated_hours) * 100, 100)}
                className="h-2"
              />
            </div>
          )}

          {/* Recent Activity */}
          {jobCard.time_logs && jobCard.time_logs.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Recent Activity</h4>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {[...jobCard.time_logs]
                  .sort((a, b) => new Date(b.log_date || b.updated_at || 0) - new Date(a.log_date || a.updated_at || 0))
                  .slice(0, 3)
                  .map((log, index) => {
                  const rawDate = log.log_date || log.date || log.updated_at;
                  const parsed = rawDate ? new Date(rawDate) : null;
                  const dateLabel = parsed && !isNaN(parsed.getTime()) ? parsed.toLocaleDateString() : '—';
                  const sortedSessions = Array.isArray(log.sessions)
                    ? [...log.sessions].sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0))
                    : [];
                  return (
                  <div key={index} className="p-2 border rounded text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{log.technician_name}</span>
                      <span className="text-gray-500">{dateLabel}</span>
                    </div>
                    <div className="text-gray-600">
                      Total: {formatDuration(log.total_time_minutes)} • 
                      Billable: {formatDuration(log.billable_time_minutes)}
                    </div>
                    {log.activity_breakdown && Object.keys(log.activity_breakdown).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(log.activity_breakdown).map(([activity, minutes]) => (
                          <Badge
                            key={activity}
                            variant="outline"
                            className="text-xs"
                          >
                            {activity}: {formatDuration(minutes)}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {sortedSessions.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid={`recent-activity-sessions-${index}`}>
                        {sortedSessions.map((session, sIdx) => {
                          const sTime = session.started_at ? new Date(session.started_at) : null;
                          const eTime = session.ended_at ? new Date(session.ended_at) : null;
                          const startLabel = sTime && !isNaN(sTime.getTime())
                            ? sTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '';
                          const endLabel = eTime && !isNaN(eTime.getTime())
                            ? eTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '';
                          return (
                            <div key={sIdx} className="flex gap-2 text-xs bg-amber-50 border border-amber-100 rounded px-2 py-1">
                              {startLabel && <span className="font-mono text-amber-700 shrink-0">{startLabel}{endLabel ? ` - ${endLabel}` : ''}</span>}
                              {session.notes && <span className="text-gray-700 whitespace-pre-wrap">{session.notes}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TimeTracker;
